import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["file", "show", "title", "author", "sourceUrl", "progress", "progressBar", "status", "submit"]

  async upload(event) {
    event.preventDefault()

    const file = this.fileTarget.files[0]
    if (!file) {
      this.showStatus("Please select an audio file.", "error")
      return
    }

    this.submitTarget.disabled = true
    this.progressTarget.classList.remove("hidden")
    this.showStatus("Requesting upload URL...", "info")

    try {
      // Step 1: Get presigned URL
      const presignResponse = await fetch("/items/presign_upload", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          filename: file.name,
          title: this.combinedTitle,
          author: this.authorTarget.value || null,
          source_url: this.sourceUrlTarget.value || null,
          content_type: file.type || "audio/mpeg"
        })
      })

      if (!presignResponse.ok) {
        const error = await presignResponse.json()
        throw new Error(error.error || "Failed to get upload URL")
      }

      const { upload_url, item_id } = await presignResponse.json()

      // Step 2: Upload directly to GCS
      this.showStatus("Uploading...", "info")
      await this.uploadToGCS(upload_url, file)

      // Step 3: Notify server upload is complete
      this.showStatus("Processing...", "info")
      const completeResponse = await fetch(`/items/${item_id}/complete_upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      if (!completeResponse.ok) {
        throw new Error("Failed to complete upload")
      }

      this.showStatus("Upload complete! Transcribing in background...", "success")
      this.fileTarget.value = ""
      this.showTarget.value = ""
      this.titleTarget.value = ""
      this.authorTarget.value = ""
      this.sourceUrlTarget.value = ""

      // Reload after a brief delay so the user sees the success message
      setTimeout(() => { window.location.reload() }, 1500)
    } catch (error) {
      this.showStatus(error.message, "error")
      this.submitTarget.disabled = false
      this.progressTarget.classList.add("hidden")
    }
  }

  uploadToGCS(url, file) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          this.progressBarTarget.style.width = `${percent}%`
          this.showStatus(`Uploading... ${percent}%`, "info")
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this.progressBarTarget.style.width = "100%"
          resolve()
        } else {
          reject(new Error(`Upload failed (HTTP ${xhr.status})`))
        }
      })

      xhr.addEventListener("error", () => reject(new Error("Upload failed")))
      xhr.addEventListener("abort", () => reject(new Error("Upload aborted")))

      xhr.open("PUT", url)
      xhr.setRequestHeader("Content-Type", file.type || "audio/mpeg")
      xhr.send(file)
    })
  }

  showStatus(message, type) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden", "text-green-400", "text-red-400", "text-text-muted")
    switch (type) {
      case "success": this.statusTarget.classList.add("text-green-400"); break
      case "error":   this.statusTarget.classList.add("text-red-400"); break
      default:        this.statusTarget.classList.add("text-text-muted"); break
    }
  }

  get combinedTitle() {
    const show = this.showTarget.value.trim()
    const episode = this.titleTarget.value.trim()
    if (show && episode) return `${show}: ${episode}`
    return show || episode || null
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
